import { Flex, Skeleton, Text } from '@backstage/ui';
import { type AsyncValue } from '../useAsyncValue';
import { NeedsYouList } from './NeedsYouList';
import { type MyPullRequest, pullRequestItem } from './myPullRequests';
import { timeAgo } from './time';
import { NeedsYouItem, PRIORITY_ORDER, URGENCY_ORDER } from './types';

type Row = { item: NeedsYouItem; draft: boolean };

/**
 * Every open pull request of the user, most actionable first. A pull request
 * with a Needs you reason (changes requested, checks failing, ready to merge,
 * idle...) carries it; the rest say whether they wait for review or are
 * drafts. Order: your move, then wrap-up, then the rest, drafts last; within
 * that, the stronger tone and then the oldest.
 */
export const openPullRequestRows = (
  prs: MyPullRequest[],
  now = Date.now(),
): NeedsYouItem[] =>
  prs
    .map((pr): Row => {
      const reason = pullRequestItem(pr, now);
      if (reason) return { item: reason, draft: pr.isDraft };
      let label = 'Open';
      if (pr.isDraft) label = 'Draft';
      else if (pr.reviewDecision === 'REVIEW_REQUIRED') {
        label = 'Waiting for review';
      }
      return {
        draft: pr.isDraft,
        item: {
          id: `github-mine-${pr.id}`,
          source: 'github-my-prs',
          priority: 'background',
          urgency: pr.isDraft ? 'low' : 'normal',
          label,
          meta: `${pr.repository.nameWithOwner} #${pr.number} · updated ${timeAgo(
            pr.updatedAt,
            now,
          )}`,
          title: pr.title,
          action: { label: 'Open', href: pr.url, external: true },
          since: Date.parse(pr.updatedAt),
        },
      };
    })
    .sort(
      (a, b) =>
        PRIORITY_ORDER.indexOf(a.item.priority) -
          PRIORITY_ORDER.indexOf(b.item.priority) ||
        Number(a.draft) - Number(b.draft) ||
        URGENCY_ORDER.indexOf(a.item.urgency) -
          URGENCY_ORDER.indexOf(b.item.urgency) ||
        (a.item.since ?? Infinity) - (b.item.since ?? Infinity),
    )
    .map(row => row.item);

/** The user's open pull requests, in the Needs you row style. */
export const OpenPullRequestsTab = ({
  prs,
  onAction,
}: {
  prs: AsyncValue<MyPullRequest[] | undefined>;
  onAction: (item: NeedsYouItem) => void;
}) => {
  // No answer yet, including the render right after the tab is opened and
  // before the fetch has started: skeletons. A refresh keeps the rows.
  if (!prs.value && !prs.error) {
    return (
      <Flex direction="column" gap="2" role="progressbar" aria-label="Loading">
        {[0, 1, 2].map(i => (
          <Skeleton key={i} width="100%" height={48} rounded />
        ))}
      </Flex>
    );
  }
  if (prs.error) {
    return (
      <Text as="p" variant="body-small" color="secondary">
        Couldn't load your pull requests: {prs.error.message}
      </Text>
    );
  }
  if (!prs.value || prs.value.length === 0) {
    return (
      <Flex justify="center" py="4">
        <Text variant="body-medium" color="secondary">
          No open pull requests.
        </Text>
      </Flex>
    );
  }
  return (
    <NeedsYouList
      label="Your open pull requests"
      items={openPullRequestRows(prs.value)}
      onAction={onAction}
    />
  );
};
